import type {
  PlatformNotificationItem,
  PlatformNotificationStats,
  PlatformNotificationsListResponse,
} from '../types/platformNotification.types';
import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

type StatsMeta = PaginationMeta & { stats?: PlatformNotificationStats };

function buildList(
  data: PlatformNotificationItem[] | undefined,
  meta?: StatsMeta,
  perPage = 15,
): PlatformNotificationsListResponse {
  const current = meta?.current_page ?? 1;
  const total = meta?.total ?? (Array.isArray(data) ? data.length : 0);
  const last = meta?.last_page ?? 1;
  return {
    current_page: current,
    data: Array.isArray(data) ? data : [],
    per_page: meta?.per_page ?? perPage,
    total,
    last_page: last,
    stats: meta?.stats,
  };
}

export async function fetchPlatformNotificationStats(): Promise<
  | { ok: true; stats: PlatformNotificationStats }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const res = await apiFetch('/platform/notifications/stats');
  const out = await parseJsonResponse<PlatformNotificationStats>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, stats: out.data };
}

export async function fetchPlatformNotifications(
  page = 1,
  perPage = 15,
  filters?: { search?: string; category?: string },
): Promise<
  | { ok: true; list: PlatformNotificationsListResponse }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (filters?.search) params.set('search', filters.search);
  if (filters?.category) params.set('category', filters.category);

  const res = await apiFetch(`/platform/notifications?${params.toString()}`);
  const out = await parseJsonResponse<PlatformNotificationItem[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, list: buildList(out.data, out.meta as StatsMeta | undefined, perPage) };
}

export async function markPlatformNotificationRead(
  id: number,
): Promise<
  | { ok: true; item: PlatformNotificationItem }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const res = await apiFetch(`/platform/notifications/${id}/read`, { method: 'PATCH' });
  const out = await parseJsonResponse<PlatformNotificationItem>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, item: out.data };
}

export async function markAllPlatformNotificationsRead(): Promise<
  | { ok: true; updated: number }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const res = await apiFetch('/platform/notifications/read-all', { method: 'POST' });
  const out = await parseJsonResponse<{ updated: number }>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, updated: out.data.updated ?? 0 };
}

export async function deletePlatformNotification(
  id: number,
): Promise<{ ok: true } | { ok: false; unauthorized?: boolean; message: string }> {
  const res = await apiFetch(`/platform/notifications/${id}`, { method: 'DELETE' });
  const out = await parseJsonResponse<null>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}
